import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap} from 'rxjs/operators';
import * as moment from 'moment';
import { CurrentUser } from '../model/CurrentUser';
import {UserService} from '../../../services/user.service';
import {SignalrService} from '../../../services/socket/signalr.service';
import {MatDialog} from '@angular/material/dialog';
import {AuthenticationResponseModel} from '../model/authentication-response.model';
import {AuthenticationRequestModel} from '../model/authentication-request.model';
import {InterceptorSkipHeader} from '../helpers/authinterceptor';
import { Globals } from '../../../globals';
import {AuthenticationRevokeModel} from '../model/authentication-revoke.model';
import {AuthDataModel} from '../model/auth-data.model';
import {RefreshTokenService} from './refresh-token.service';
import { PasswordChangeRequest } from '../model/password-change-request.model';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService
{

  currentUser: BehaviorSubject<CurrentUser>;
  private currentPatientSubject: BehaviorSubject<CurrentUser>;
  currentPatient: Observable<CurrentUser>;
  currentUserRolesIds: string[] = [];

  userDisplayName: string;
  private readonly loginurl = '';

  constructor(private readonly http: HttpClient,
              private readonly dialogRef: MatDialog,
              private readonly signalrService: SignalrService,
              private readonly globals: Globals,
              private readonly refreshTokenService: RefreshTokenService,
  )
  {
    this.currentUser = new BehaviorSubject<CurrentUser>
      (JSON.parse(localStorage.getItem('storedUser')));

    this.currentPatientSubject = new BehaviorSubject<CurrentUser>
      (JSON.parse(localStorage.getItem('storedPatient')));

    this.currentPatient = this.currentPatientSubject.asObservable();
  }

  public loginJwt(username: string, password: string, locId: string, workstationName: string, loginType: string =''): Observable<AuthenticationResponseModel> {
    this.currentUserRolesIds = [];
    const authReq: AuthenticationRequestModel = new AuthenticationRequestModel();
    authReq.Username = username;
    authReq.Password = password;
    authReq.LocationId = locId;
    authReq.LoginType = loginType;
    authReq.WorkstationName = workstationName;
    return this.http.post<AuthenticationResponseModel>(
      this.loginurl + '/api/jwtauthentication/login',
      authReq,
      {headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true})
      .pipe(
        tap((_) => {
          if (_.isSuccess) {
            this.globals.LOCID = locId;
            localStorage.setItem('TDOCID', _.tdoCId);
            this.globals.TDOCID = _.tdoCId;
            this.setToken(_.token);
            this.refreshTokenService.scheduleNextRefreshToken();
          }
        })
      );
  }

  autoLoginPatient(token: string) {
    this.currentUserRolesIds = [];
    const authReq: AuthenticationRequestModel = new AuthenticationRequestModel();
    authReq.Username = 'autologin';
    authReq.Password = token;

    return this.http.post<AuthenticationResponseModel>(
      this.loginurl + '/api/jwtauthentication/auto-login',
      authReq,
      {headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true})
      .pipe(
        tap((_) => {
          if (_.isSuccess) {
            localStorage.setItem('TDOCID', _.tdoCId);
            localStorage.setItem('TDOPatientAuthToken', _.token);
            this.storePatientTokenResult(_);
            this.refreshTokenService.scheduleNextPatientRefreshToken();
          }
        })
      );
  }

  public autoLoginJwt(): Observable<string> {
    this.currentUserRolesIds = [];
    return this.http.post<string>(
      this.loginurl + '/api/jwtauthentication/get-auto-login-token',
      {headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true});
  }

  getToken() {
    return localStorage.getItem('TDOauthToken');
  }

  setToken(token: string) {
    localStorage.setItem('TDOauthToken', token);
  }

  clearToken() {
    localStorage.removeItem('TDOauthToken');
  }

  checkIfTokenIsValid(): Observable<any> {
    return this.http.get<any>(this.loginurl + '/api/jwtauthentication/isvalid');
  }

  public get currentUserValue(): CurrentUser
  {
    return this.currentUser.value;
  }

  getrolesapi(): any {
    return this.http.get<string>(this.loginurl + '/api/auth/GetAllUserPracticeRoles');
  }

  public storePatientTokenResult(authResult: AuthenticationResponseModel): boolean {
    
    if ((authResult.token !== '') && (authResult.token != null) && authResult.isSuccess) {
      const newuser: CurrentUser = this.getCurrentUser(authResult);      
      localStorage.setItem('storedPatient', JSON.stringify(newuser));
      localStorage.setItem('storedUser', JSON.stringify(newuser));
      localStorage.setItem('TDOEnterpriseId', authResult.tdoEnterpriseId);
      this.globals.currentPatientId = authResult.patientId;
      this.globals.LOCID = authResult.locId;

      this.currentPatientSubject.next(newuser);
      this.currentUser.next(newuser);

      return true;
    }
    else {
      return false;
    }
  }

  public storeReferringDoctorTokenResult(authResult: AuthenticationResponseModel): boolean {
    
    if ((authResult.token !== '') && (authResult.token != null) && authResult.isSuccess) {
      const newuser: CurrentUser = this.getCurrentUser(authResult);      
      localStorage.setItem('storedReferrerUser', JSON.stringify(newuser));
      localStorage.setItem('TDOEnterpriseId', authResult.tdoEnterpriseId);
      localStorage.setItem("isReferrerRole", "true");
      this.currentUser.next(newuser);

      return true;
    }
    else {
      return false;
    }
  }

  public storetokenResult(authResult: AuthenticationResponseModel): boolean {
    if ((authResult.token !== '') && (authResult.token != null) && authResult.isSuccess) {
      const newuser: CurrentUser = this.getCurrentUser(authResult);

      localStorage.setItem('storedUser', JSON.stringify(newuser));
      localStorage.setItem('TDOEnterpriseId', authResult.tdoEnterpriseId);

      this.currentUser.next(newuser);

      return true;
    }
    else {
      return false;
    }
  }

  getCurrentUser(authResult: AuthenticationResponseModel) {
    const authData: AuthDataModel = new AuthDataModel();
      authData.TDOCID = authResult.tdoCId;
      authData.TDOGuid = authResult.tdoGuid;
      authData.access_token = authResult.token;
      authData.userdisplayname = authResult.userDisplayName;
      authData.userfullname = authResult.userFullName;
      authData.userid = authResult.userId;
      authData.usernickname = authResult.userNickName;
      authData.userroles = authResult.userRoles;
      authData.webclientid = authResult.webClientId;
      authData.WorkStationID = authResult.workStationId;
      authData.tzId = authResult.tzId;
      authData.tzOffsetMins = authResult.tzoffsetMins;
      authData.patientid = authResult.patientId;
      authData.doctorId = authResult.doctorId;

      UserService.authToken = authResult.token;
      const token = authResult.token;
      this.userDisplayName = authResult.userDisplayName;
      const newuser: CurrentUser = new CurrentUser();
      newuser.webclientid = authResult.webClientId;
      newuser.TDOCID = authResult.tdoCId;
      newuser.authdata = authData;
      newuser.callingapp = 'TDOx';
      newuser.firstname = '';
      newuser.lastname = '';
      newuser.nickname = authResult.userNickName;
      newuser.fullname = authResult.userFullName;
      newuser.TDOGuid = authResult.tdoGuid;
      newuser.userdisplayname = authResult.userDisplayName;
      newuser.userroles = authResult.userRoles;
      newuser.authtoken = token;

      return newuser;

  }
  getExpiration()
  {
    const expiration = localStorage.getItem('expires_at');
    const expiresAt = JSON.parse(expiration);
    return moment(expiresAt);
  }

  public isLoggedIn() {
    return moment().isBefore(this.getExpiration());
  }

  logout() {
    this.currentUserRolesIds = [];
    this.currentUser.next(null);
    const revokeTokenReq: AuthenticationRevokeModel = new AuthenticationRevokeModel();
    revokeTokenReq.Token = this.getToken();
    this.http.post<string>(
      this.loginurl + '/api/jwtauthentication/revoke-token', revokeTokenReq,
      { headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true}
    ).subscribe();

    this.clearToken();
    this.refreshTokenService.clearRefreshTimeout();
    // remove user from local storage to log user out. handleAuthError or inactivity monitor will clear data
    //localStorage.clear();
    
    localStorage.removeItem('TDOauthToken');
    localStorage.removeItem('storedUser');
    localStorage.removeItem('TDOPatientAuthToken');
    localStorage.removeItem('storedPatient');
    localStorage.removeItem('storedReferrerUser');
    localStorage.removeItem('isPatientRole');
    localStorage.removeItem('isReferrerRole');
    localStorage.removeItem('loggedInDoctorId');
    localStorage.removeItem('storageAccount');
    localStorage.removeItem('LOCenumCountryId');
    localStorage.removeItem('TDOEnterpriseId');
    localStorage.removeItem('TDOCID');
    localStorage.removeItem('WorkStationID');
    localStorage.removeItem('appointmentData');    
    localStorage.removeItem('currentPatientCaseId'); 
    localStorage.removeItem('currentPatientCaseWorkItemEventID'); 
    localStorage.removeItem('currentPatientFullName'); 
    localStorage.removeItem('currentPatientSelectedCaseItem');
    localStorage.removeItem('currentProviderDoctorContactID');
    localStorage.removeItem('currentProviderDoctorID');
    localStorage.removeItem('currentProviderDoctorUserID');    
    localStorage.removeItem('currentTreatmentPlanID');    
    
    this.currentUser.next(null);
    this.currentPatientSubject.next(null);
    this.signalrService.disconnect();
    this.dialogRef.closeAll();

  }

  getstoredToken(): string {
    if (localStorage.getItem('TDOauthToken')) {
      return localStorage.getItem('TDOauthToken');
    } else if (localStorage.getItem('TDOPatientAuthToken')) {
      return localStorage.getItem('TDOPatientAuthToken');
    }
    return localStorage.getItem('TDOauthToken');
  }

  getLoginCodeForPatient(patientId: string): Observable<string> {
    return this.http.get<string>(`api/userLogin/setUserLoginCode/${patientId}/000000AA-1000-0000-0000-000000000002`);
  }

  resetPasswordRequest(userName: any, userEmail: any, workStationName: string, loginType: string =''): Observable<AuthenticationResponseModel> {
    this.currentUserRolesIds = [];
    const authReq: AuthenticationRequestModel = new AuthenticationRequestModel();
    authReq.Username = userName;
    authReq.Email = userEmail;
    authReq.WorkstationName = workStationName;
    authReq.LoginType = loginType;
    return this.http.post<AuthenticationResponseModel>(
      this.loginurl + '/api/jwtauthentication/resetpassword',
      authReq,
      {headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true});
  }

  resetPassword(token: any, newPassword: any, confirmPassword: string): Observable<AuthenticationResponseModel> {
    const passwordChangeReq: PasswordChangeRequest = new PasswordChangeRequest();
    console.log('Reset password token: ', token);
    console.log('Reset password token encoded: ', encodeURIComponent(token));
    passwordChangeReq.Token = encodeURIComponent(token);
    passwordChangeReq.NewPassword = newPassword;
    passwordChangeReq.ConfirmPassword = confirmPassword;
    return this.http.post<AuthenticationResponseModel>(
      this.loginurl + '/api/jwtauthentication/changepassword',
      passwordChangeReq,
      {headers: new HttpHeaders().set(InterceptorSkipHeader, ''), withCredentials: true});
  }
}
